import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { 
  Users, 
  Award, 
  BookOpen, 
  CheckCircle, 
  ArrowRight, 
  Mail, 
  User, 
  GraduationCap,
  FileText,
  Globe,
  Shield
} from "lucide-react";

export default function ReviewPanel() {
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    designation: "",
    institution: "",
    expertise: "",
    qualification: "",
    experience: "",
    publications: "",
    motivation: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate form submission
    setTimeout(() => {
      // Create mailto link with form data
      const subject = encodeURIComponent("Review Panel Membership Application - " + formData.fullName);
      const body = encodeURIComponent(`
REVIEW PANEL MEMBERSHIP APPLICATION
=====================================

Full Name: ${formData.fullName}
Email: ${formData.email}
Phone: ${formData.phone}
Designation: ${formData.designation}
Institution: ${formData.institution}
Highest Qualification: ${formData.qualification}
Years of Experience: ${formData.experience}

Areas of Expertise:
${formData.expertise}

Notable Publications:
${formData.publications}

Motivation for Joining:
${formData.motivation}

=====================================
Submitted via SPA Publication Group Website
      `);
      
      window.location.href = `mailto:submissionspapublication@gmail.com?subject=${subject}&body=${body}`;
      
      toast.success("Redirecting to your email client...", {
        description: "Please send the email to complete your application."
      });
      
      setIsSubmitting(false);
    }, 1000);
  };

  return (
    <div data-testid="review-panel-page" className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-6">
              <div className="h-[1px] w-12 bg-[#C5A059]" />
              <span className="text-[#C5A059] text-sm font-medium tracking-widest uppercase">Join Our Team</span>
            </div>
            <h1 className="font-serif text-5xl sm:text-6xl font-medium text-white mb-6">
              Be a Review Panel Member
            </h1>
            <p className="text-slate-400 text-lg">
              Join our distinguished panel of academic reviewers and contribute to maintaining the highest standards of scholarly publishing.
            </p>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-[#C5A059]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-6">
            <BenefitItem 
              icon={Award}
              title="Recognition"
              description="Certificate of appreciation and listing as a reviewer"
            />
            <BenefitItem 
              icon={BookOpen}
              title="Free Publications"
              description="Complimentary copies of reviewed publications"
            />
            <BenefitItem 
              icon={Globe}
              title="Network"
              description="Connect with academics and researchers worldwide"
            />
            <BenefitItem 
              icon={Shield}
              title="Credibility"
              description="Enhance your academic profile and credibility"
            />
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Left Column - Info */}
            <div className="lg:col-span-1">
              <h2 className="font-serif text-3xl font-medium text-slate-900 mb-6">Why Join Our Review Panel?</h2>
              <p className="text-slate-600 mb-8 leading-relaxed">
                As a review panel member at SPA Publication Group, you play a crucial role in maintaining academic integrity and quality. Our reviewers are respected experts who help shape the future of academic publishing.
              </p>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-slate-950 flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-5 h-5 text-[#C5A059]" />
                  </div>
                  <div>
                    <h4 className="font-medium text-slate-900 mb-1">Quality Assurance</h4>
                    <p className="text-slate-500 text-sm">Ensure only high-quality research gets published</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-slate-950 flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-5 h-5 text-[#C5A059]" />
                  </div>
                  <div>
                    <h4 className="font-medium text-slate-900 mb-1">Flexible Commitment</h4>
                    <p className="text-slate-500 text-sm">Review at your own pace with no minimum requirements</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-slate-950 flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-5 h-5 text-[#C5A059]" />
                  </div>
                  <div>
                    <h4 className="font-medium text-slate-900 mb-1">Stay Updated</h4>
                    <p className="text-slate-500 text-sm">Access latest research in your field before publication</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-slate-950 flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-5 h-5 text-[#C5A059]" />
                  </div>
                  <div>
                    <h4 className="font-medium text-slate-900 mb-1">Professional Development</h4>
                    <p className="text-slate-500 text-sm">Enhance your academic skills and reputation</p>
                  </div>
                </div>
              </div>

              <div className="mt-10 p-6 bg-slate-950">
                <h4 className="font-serif text-lg text-white mb-2">Have Questions?</h4>
                <p className="text-slate-400 text-sm mb-4">Contact us for more information about the review panel.</p>
                <a 
                  href="mailto:submissionspapublication@gmail.com" 
                  className="text-[#C5A059] text-sm font-medium hover:underline inline-flex items-center gap-1"
                >
                  <Mail className="w-4 h-4" /> submissionspapublication@gmail.com
                </a>
              </div>
            </div>

            {/* Right Column - Form */}
            <div className="lg:col-span-2">
              <Card className="bg-white border border-slate-100 rounded-none">
                <CardContent className="p-8">
                  <h3 className="font-serif text-2xl font-medium text-slate-900 mb-2">Application Form</h3>
                  <p className="text-slate-500 mb-8">Fill in your details to apply for review panel membership.</p>

                  <form onSubmit={handleSubmit} className="space-y-6" data-testid="review-panel-form">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="fullName" className="text-slate-700">
                          <User className="w-4 h-4 inline mr-2" />Full Name *
                        </Label>
                        <Input 
                          id="fullName"
                          name="fullName"
                          value={formData.fullName}
                          onChange={handleChange}
                          required
                          placeholder="Dr. John Doe"
                          className="rounded-none border-slate-200 focus:border-[#C5A059] focus:ring-[#C5A059]"
                          data-testid="input-fullname"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email" className="text-slate-700">
                          <Mail className="w-4 h-4 inline mr-2" />Email Address *
                        </Label>
                        <Input 
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          placeholder="john.doe@university.edu"
                          className="rounded-none border-slate-200 focus:border-[#C5A059] focus:ring-[#C5A059]"
                          data-testid="input-email"
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="phone" className="text-slate-700">Phone Number</Label>
                        <Input 
                          id="phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="+91 98765 43210"
                          className="rounded-none border-slate-200 focus:border-[#C5A059] focus:ring-[#C5A059]"
                          data-testid="input-phone"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="designation" className="text-slate-700">Current Designation *</Label>
                        <Input 
                          id="designation"
                          name="designation"
                          value={formData.designation}
                          onChange={handleChange}
                          required
                          placeholder="Professor / Associate Professor / Advocate"
                          className="rounded-none border-slate-200 focus:border-[#C5A059] focus:ring-[#C5A059]"
                          data-testid="input-designation"
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="institution" className="text-slate-700">Institution / Organization *</Label>
                        <Input 
                          id="institution"
                          name="institution"
                          value={formData.institution}
                          onChange={handleChange}
                          required
                          placeholder="University / Law Firm / Research Institute"
                          className="rounded-none border-slate-200 focus:border-[#C5A059] focus:ring-[#C5A059]"
                          data-testid="input-institution"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="qualification" className="text-slate-700">
                          <GraduationCap className="w-4 h-4 inline mr-2" />Highest Qualification *
                        </Label>
                        <Input 
                          id="qualification"
                          name="qualification"
                          value={formData.qualification}
                          onChange={handleChange}
                          required
                          placeholder="Ph.D. / LL.M. / M.Phil."
                          className="rounded-none border-slate-200 focus:border-[#C5A059] focus:ring-[#C5A059]"
                          data-testid="input-qualification"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="experience" className="text-slate-700">Years of Experience in Academia/Practice *</Label>
                      <Input 
                        id="experience"
                        name="experience"
                        value={formData.experience}
                        onChange={handleChange}
                        required
                        placeholder="e.g., 10 years"
                        className="rounded-none border-slate-200 focus:border-[#C5A059] focus:ring-[#C5A059]"
                        data-testid="input-experience"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="expertise" className="text-slate-700">
                        <FileText className="w-4 h-4 inline mr-2" />Areas of Expertise *
                      </Label>
                      <Textarea 
                        id="expertise"
                        name="expertise"
                        value={formData.expertise}
                        onChange={handleChange}
                        required
                        placeholder="e.g., Constitutional Law, Criminal Law, Corporate Law, Family Law, Human Rights, Intellectual Property, etc."
                        rows={3}
                        className="rounded-none border-slate-200 focus:border-[#C5A059] focus:ring-[#C5A059]"
                        data-testid="input-expertise"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="publications" className="text-slate-700">Notable Publications (if any)</Label>
                      <Textarea 
                        id="publications"
                        name="publications"
                        value={formData.publications}
                        onChange={handleChange}
                        placeholder="List your major publications, research papers, or books"
                        rows={3}
                        className="rounded-none border-slate-200 focus:border-[#C5A059] focus:ring-[#C5A059]"
                        data-testid="input-publications"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="motivation" className="text-slate-700">Why do you want to join our review panel?</Label>
                      <Textarea 
                        id="motivation"
                        name="motivation"
                        value={formData.motivation}
                        onChange={handleChange}
                        placeholder="Share your motivation for joining and how you can contribute"
                        rows={3}
                        className="rounded-none border-slate-200 focus:border-[#C5A059] focus:ring-[#C5A059]"
                        data-testid="input-motivation"
                      />
                    </div>

                    <div className="bg-slate-50 p-4 border border-slate-200">
                      <p className="text-slate-600 text-sm">
                        <CheckCircle className="w-4 h-4 inline mr-2 text-[#C5A059]" />
                        By submitting this form, you agree to receive communications from SPA Publication Group regarding review panel activities and publication opportunities.
                      </p>
                    </div>

                    <Button 
                      type="submit" 
                      disabled={isSubmitting}
                      className="w-full bg-slate-950 hover:bg-slate-800 text-white rounded-none py-6 text-lg"
                      data-testid="submit-application-btn"
                    >
                      {isSubmitting ? "Processing..." : "Submit Application"}
                      <ArrowRight className="ml-2 w-5 h-5" />
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-slate-950">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-serif text-4xl font-medium text-white mb-6">Looking to Publish Instead?</h2>
          <p className="text-slate-400 text-lg mb-10">
            If you have a manuscript ready for publication, we'd love to hear from you.
          </p>
          <div className="flex justify-center gap-4">
            <Button asChild className="bg-[#C5A059] hover:bg-[#a88a48] text-slate-950 rounded-none px-8 py-6">
              <Link to="/submission">Submit Manuscript <ArrowRight className="ml-2 w-4 h-4" /></Link>
            </Button>
            <Button asChild variant="outline" className="border-slate-600 text-white hover:bg-white/10 rounded-none px-8 py-6">
              <Link to="/services">View Services</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}

function BenefitItem({ icon: Icon, title, description }) {
  return (
    <div className="flex items-start gap-4">
      <div className="w-12 h-12 bg-slate-950 flex items-center justify-center flex-shrink-0">
        <Icon className="w-6 h-6 text-[#C5A059]" />
      </div>
      <div>
        <h3 className="font-medium text-slate-950 mb-1">{title}</h3>
        <p className="text-slate-800 text-sm">{description}</p>
      </div>
    </div>
  );
}
